import { cn } from '@my/ui';
import { Check, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  ALERT_ACCENT,
  ASSIGN_STATE_ACCENT,
  ASSIGN_STATE_LABEL,
  ASSIGN_STATE_ORDER,
  assignState,
  type ResourceCardVM,
} from '../lib/planResources';
import { CapBadge } from './PlanShared';

interface VehiclePickerPanelProps {
  open: boolean;
  /** Driver the vehicle is being linked to. */
  driverName: string | null;
  /** Candidate vehicles — the cockpit's depot-scoped Vehicles pool. */
  vehicles: ResourceCardVM[];
  /** The driver's current default vehicle, if any — enables the unlink action. */
  currentVehicleName: string | null;
  onClose: () => void;
  onLink: (vehicleId: string) => void;
  onUnlink: () => void;
}

/**
 * Vehicle picker — slides in from the right to link a vehicle to a driver,
 * opened from a driver card's "No vehicle" / "Choose vehicle" action.
 *
 * Deliberately sits at `z-30`, one layer below the "my i" rail (`z-40`), so the
 * intelligence panel still overlays it rather than the two fighting for the same
 * space.
 */
export function VehiclePickerPanel({
  open,
  driverName,
  vehicles,
  currentVehicleName,
  onClose,
  onLink,
  onUnlink,
}: VehiclePickerPanelProps) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matched = q
      ? vehicles.filter((v) =>
          [v.title, v.subtitle, v.meta ?? ''].join(' ').toLowerCase().includes(q),
        )
      : vehicles;
    // Readiness first — Free, In use, Warn, Stop — then alphabetical inside each
    // group, so what you can actually take is at the top. Copy before sorting:
    // `vehicles` is a prop and must not be mutated.
    return [...matched].sort(
      (a, b) =>
        ASSIGN_STATE_ORDER[assignState(a)] - ASSIGN_STATE_ORDER[assignState(b)] ||
        a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }),
    );
  }, [vehicles, search]);

  return (
    <aside
      aria-hidden={!open}
      className={cn(
        'absolute inset-y-0 right-0 z-30 flex w-[26rem] max-w-[92%] flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-300 ease-out',
        open ? 'translate-x-0' : 'pointer-events-none translate-x-full',
      )}
    >
      {/* Header — same dark bar as the my i rail so the two read as siblings. */}
      <div className="flex w-full items-center gap-2 border-b border-slate-700 bg-slate-800 px-3 py-3">
        <div className="min-w-0 leading-tight">
          <div className="text-xs font-medium text-slate-100">Choose vehicle</div>
          <div className="truncate text-[10px] text-slate-400">
            {driverName ?? 'Select a driver'}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close vehicle picker"
          title="Close"
          className="ml-auto shrink-0 rounded-full p-0.5 text-slate-300 transition-colors hover:bg-slate-700 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Search */}
      <div className="border-b border-slate-200 px-3 py-2">
        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vehicles…"
            className="w-full rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
      </div>

      {/* Current link — the only route back to "no vehicle" from here. */}
      {currentVehicleName && (
        <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
          <span className="min-w-0 flex-1 truncate text-[11px] text-slate-600">
            Currently <span className="font-medium text-slate-800">{currentVehicleName}</span>
          </span>
          <button
            type="button"
            onClick={onUnlink}
            className="shrink-0 rounded-md border border-amber-300 bg-white px-2 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-50"
          >
            Unlink
          </button>
        </div>
      )}

      {/* List */}
      <div className="min-h-0 flex-1 overflow-auto p-2">
        {vehicles.length === 0 ? (
          <p className="py-6 text-center text-xs italic text-slate-400">
            No vehicles at this depot. Add trucks or rigids in Settings → Resources.
          </p>
        ) : filtered.length === 0 ? (
          <p className="py-6 text-center text-xs italic text-slate-400">
            No vehicles match “{search.trim()}”.
          </p>
        ) : (
          <ul className="space-y-1">
            {filtered.map((v) => (
              <VehicleRow key={v.id} v={v} onLink={onLink} />
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

/**
 * One selectable vehicle. The readiness state is computed once here and drives
 * both the pill and whether linking is allowed at all.
 */
function VehicleRow({ v, onLink }: { v: ResourceCardVM; onLink: (id: string) => void }) {
  const state = assignState(v);
  // A blocking compliance problem (expired insurance / MOT with a Stop action)
  // means this vehicle mustn't go out — so don't let it be linked.
  const blocked = state === 'stop';

  return (
    <li className="flex items-start gap-1 rounded border border-slate-200 px-2 py-1">
      <div className="min-w-0 flex-1">
        {/* Reg + type sit on the title's line — most vehicles are named
            after their registration, so stacking them just repeated the
            same string down the whole list. */}
        <div className="flex items-baseline justify-between gap-2">
          <span className="min-w-0 truncate">
            <span className="text-xs font-semibold text-slate-800">{v.title}</span>
            {/* Who currently has it — the reason behind the "In use" pill.
                Plural: a shared truck lists every driver that defaults to it. */}
            {v.linkedDriverNames.length > 0 && (
              <span
                className="ml-1.5 text-[11px] font-medium text-amber-600"
                title={v.linkedDriverNames.join(', ')}
              >
                {v.linkedDriverNames.join(', ')}
              </span>
            )}
          </span>
          {v.subtitle && v.subtitle !== '—' && v.subtitle !== v.title && (
            <span className="shrink-0 font-mono text-[11px] text-slate-500">{v.subtitle}</span>
          )}
        </div>
        {/* Where it's based, then where it actually is — the latter a
            placeholder until telematics lands, same as the odometer below. */}
        <div className="truncate text-[11px] text-slate-500">
          Depot: {v.depotLabel ?? '—'}
        </div>
        <div className="truncate text-[10px] italic text-slate-400">
          Location: {v.currentLocationLabel ?? '—'}
        </div>
        {/* Why it's flagged — blocking reasons first, coloured to match
            the readiness pill. Sits above the tags so a Stop is read
            before the capabilities. */}
        {v.alerts.map((a) => (
          <div key={a.message} className={cn('text-[10px] font-medium', ALERT_ACCENT[a.level])}>
            {a.message}
          </div>
        ))}
        {(v.typeChip || v.caps.length > 0) && (
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {/* What the unit is, ahead of what it can carry. Rigids show
                their equipment type here instead of a bare "Rigid". */}
            {v.typeChip && (
              <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-500">
                {v.typeChip}
              </span>
            )}
            {v.caps.slice(0, 4).map((cap) => (
              <CapBadge
                key={cap.label}
                label={cap.label}
                variant={cap.serviceTag ? 'tag' : 'cert'}
              />
            ))}
            {v.caps.length > 4 && (
              <span className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
                +{v.caps.length - 4}
              </span>
            )}
          </div>
        )}
        {/* Odometer — placeholder until the telematics feed supplies it. */}
        <div className="mt-0.5 text-[10px] italic text-slate-400">
          Odometer: {v.odometerKm == null ? '—' : `${v.odometerKm.toLocaleString()} km`}
        </div>
      </div>
      {/* Action + assignment readiness, stacked on the right. */}
      <div className="flex shrink-0 flex-col items-center gap-1">
        {v.available ? (
          <button
            type="button"
            onClick={() => onLink(v.id)}
            disabled={blocked}
            title={blocked ? v.alerts.find((a) => a.level === 'stop')?.message : undefined}
            className="shrink-0 rounded-full bg-emerald-600 px-1 py-1 text-[11px] font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:hover:bg-slate-300"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        ) : (
          <span className="shrink-0 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[10px] font-medium text-red-500">
            Inactive
          </span>
        )}
        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-[10px] font-medium',
            ASSIGN_STATE_ACCENT[state],
          )}
        >
          {ASSIGN_STATE_LABEL[state]}
        </span>
      </div>
    </li>
  );
}
